import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/utils";
import { X, UserCog, Loader2, CheckCircle2 } from "lucide-react";

export default function EditProfileModal({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const close = () => {
    setErr("");
    setDone(false);
    setName(user?.name || "");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const trimmed = name.trim();
    if (!trimmed) {
      setErr("Name cannot be empty");
      return;
    }
    if (trimmed === user?.name) {
      setErr("Enter a different name to update");
      return;
    }
    setLoading(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      await refresh();
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 fade-in" onClick={close}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
        <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <UserCog className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h2 className="font-heading font-semibold">Edit profile</h2>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={close} data-testid="edit-profile-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" strokeWidth={1.4} />
            <div>
              <div className="font-heading text-lg font-semibold">Profile updated</div>
              <div className="text-sm text-stone-500 mt-1">Your new name will appear across the app.</div>
            </div>
            <button className="btn btn-primary" onClick={close} data-testid="edit-profile-done-btn">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="label">Display name</label>
              <input
                required
                autoFocus
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                placeholder="e.g. Rakesh Janardan"
                data-testid="edit-profile-name"
              />
              <div className="text-[11px] text-stone-500 mt-1">Shown in the sidebar and the Users list.</div>
            </div>

            <div className="bg-stone-50 border border-stone-200 rounded-md px-3 py-2 text-xs text-stone-600">
              <div className="font-semibold text-stone-700 mb-0.5">Account email</div>
              <div className="font-mono">{user?.email}</div>
              <div className="text-stone-500 mt-1">Email cannot be changed yet — contact an admin if needed.</div>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-outline" onClick={close} disabled={loading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading} data-testid="edit-profile-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className="w-4 h-4" strokeWidth={1.6} />}
                Save changes
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
